import axios from 'axios';
import config from '../config/config.json';
import { Tour, Discord, Ruleset, TourParticipant, TourParticipantStatus } from '../models/tour';

// Verify authorization token exists
const verifyToken = () => {
  const token = localStorage.getItem('session_token');
  if (!token) {
    throw new Error('Authentication required');
  }
};

// Get headers with session token
export const getHeaders = () => {
  const sessionToken = localStorage.getItem('session_token');
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (sessionToken) {
    headers['session-token'] = sessionToken;
  }

  return headers;
};

/**
 * Fetch all tours
 */
export async function getAllTours(): Promise<Tour[]> {
  verifyToken();
  const response = await axios.get(`${config.backend_base_url}api/tours`, {
    headers: getHeaders(),
  });
  return response.data;
}

/**
 * Fetch a specific tour by ID
 */
export async function getTourById(id: string): Promise<Tour> {
  verifyToken();
  const response = await axios.get(`${config.backend_base_url}api/tour/${id}`, {
    headers: getHeaders(),
  });
  return response.data;
}

/**
 * Create a new tour
 */
export async function createTour(tour: Tour): Promise<Tour> {
  verifyToken();
  const response = await axios.post(`${config.backend_base_url}api/tour`, tour, {
    headers: getHeaders(),
  });
  return response.data;
}

/**
 * Update an existing tour
 */
export async function updateTour(id: string, tour: Tour): Promise<Tour> {
  verifyToken();
  const response = await axios.put(`${config.backend_base_url}api/tour/${id}`, tour, {
    headers: getHeaders(),
  });
  return response.data;
}

/**
 * Delete a tour
 */
export async function deleteTour(id: string): Promise<void> {
  verifyToken();
  await axios.delete(`${config.backend_base_url}api/tour/${id}`, {
    headers: getHeaders(),
  });
}

/**
 * Fetch all rulesets
 */
export async function getAllRulesets(): Promise<Ruleset[]> {
  verifyToken();
  const response = await axios.get(`${config.backend_base_url}api/rulesets`, {
    headers: getHeaders(),
  });
  return response.data;
}

/**
 * Fetch all Discord teams
 */
export async function getAllDiscords(): Promise<Discord[]> {
  verifyToken();
  const response = await axios.get(`${config.backend_base_url}api/teams`, {
    headers: getHeaders(),
  });
  return response.data;
}

/**
 * Fetch participants for a specific tour
 */
export async function getTourParticipants(tourId: string): Promise<TourParticipant[]> {
  verifyToken();
  const response = await axios.get(`${config.backend_base_url}api/tour/${tourId}/participants`, {
    headers: getHeaders(),
  });
  return response.data;
}

/**
 * Join a tour
 */
export async function joinTour(tourId: string): Promise<void> {
  verifyToken();
  await axios.post(
    `${config.backend_base_url}api/tour/${tourId}/join`,
    {},
    {
      headers: getHeaders(),
    }
  );
}

/**
 * Leave a tour
 */
export async function leaveTour(tourId: string): Promise<void> {
  verifyToken();
  await axios.post(
    `${config.backend_base_url}api/tour/${tourId}/leave`,
    {},
    {
      headers: getHeaders(),
    }
  );
}

/**
 * Get participant status for a specific tour
 */
export async function getTourParticipantStatus(tourId: string): Promise<TourParticipantStatus> {
  verifyToken();
  const response = await axios.get(
    `${config.backend_base_url}api/tour/${tourId}/participant-status`,
    {
      headers: getHeaders(),
    }
  );
  return response.data;
}
